import React, { memo } from 'react';
import { Flex } from 'antd-mobile';
import PropTypes from 'prop-types';
import { ImgWrap } from 'pubBiz';
import { ClassifyRecommandCell, GoodsCell } from 'pubBiz/CommonCell';
import cx from 'classnames';
import styles from './index.modules.less';

const defaultProps = {
  data: {},
  productParams: {},
  onPress: null
};

// 商品信息数据结构
const itemInfoPropTypes = PropTypes.shape({
  id: PropTypes.string, // 商品ID
  itemId: PropTypes.string, // 商品ID（兼容字段）
  imgUrl: PropTypes.string, // 商品图片
  name: PropTypes.string, // 商品名称
  price: PropTypes.number, // 商品价格
  maxPrice: PropTypes.number, // 商品最高价格
  type: PropTypes.number // 商品类型（6：多规格商品）
});

// 卖点数据结构
const sellPointPropTypes = PropTypes.shape({
  sellPointColour: PropTypes.string, // 卖点颜色（支持'FF0000'或'#FF0000'格式）
  sellPointName: PropTypes.string // 卖点文案
});

// classifyItemRelationRespDtoList成员结构
const classifyItemRelationRespDtoPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    itemInfo: itemInfoPropTypes, // 商品信息
    sellPointList: PropTypes.arrayOf(sellPointPropTypes) // 卖点列表
  })
);

// classifyLinkRelationRespDtoList成员结构
const classifyLinkRelationRespDtoPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string, // 推荐位ID
    type: PropTypes.number, // 推荐位类型
    content: PropTypes.string, // 推荐位内容
    extend: PropTypes.object, // 扩展字段
    imgUrlApp: PropTypes.string // 推荐位图片
  })
);

// data成员结构
const dataPropTypes = PropTypes.shape({
  mainImgUrl: PropTypes.string, // 背景图
  title: PropTypes.string, // 主标题
  titleColor: PropTypes.string, // 标题颜色
  subTitle: PropTypes.string, // 副标题
  subTitleColor: PropTypes.string, // 副标题颜色
  moreText: PropTypes.string, // 进入标识
  moreColor: PropTypes.string, // 进入标识颜色
  itemBgColor: PropTypes.string, // 产品背景色
  filletValue: PropTypes.number, // 圆角值
  relativeType: PropTypes.number, // 内容目录 0: 产品目录 1: 推荐链接目录
  classifyItemRelationRespDtoList: classifyItemRelationRespDtoPropTypes, // 商品列表
  classifyLinkRelationRespDtoList: classifyLinkRelationRespDtoPropTypes // 推荐位列表
});

const propTypes = {
  width: PropTypes.number.isRequired, // 容器宽度
  data: dataPropTypes,
  productParams: PropTypes.object, // Object 商品参数
  onPress: PropTypes.func // 点击事件
};

const Main_3_5Grid = memo((props) => {
  const {
    width: cellWidth, // Any 容器宽度
    data, // Object 数据
    productParams, // Object 商品参数
    onPress // Function 点击事件
  } = props;

  const {
    mainImgUrl, // String 背景图
    title, // String 主标题
    titleColor, // String 标题颜色
    subTitle, // String 副标题
    subTitleColor, // String 副标题颜色
    moreText, // String 进入标识
    moreColor, // String 进入标识颜色
    itemBgColor, // String 产品背景色
    filletValue = 0, // Number 圆角值
    relativeType, // Number 内容目录 0: 产品目录 1: 推荐链接目录
    classifyItemRelationRespDtoList, // Array 产品列表
    classifyLinkRelationRespDtoList // Array 推荐位
  } = data;

  // 商品列表
  const renderProducts = () => {
    return (
      <Flex
        align="center"
        justify="center"
        className={styles.productList}
        style={{
          backgroundColor: itemBgColor
        }}>
        <GoodsCell
          width={`calc(${cellWidth} - 0.24rem)`}
          data={{ itemList: classifyItemRelationRespDtoList }}
          config={{
            reverse: true,
            radius: '0.08rem'
          }}
          productParams={productParams}
          horizontal
          horizontalConfig={{
            size: '2.04rem',
            count: 3,
            type: 'scroll'
          }}
        />
      </Flex>
    );
  };

  // 推荐位列表
  const renderRecommands = () => {
    // 推荐位图片的尺寸为 120 * 192
    // Todos: classifyLinkRelationRespDtoList filter 掉 displayMode === 1 是后端未过滤小程序展示内容做的保底，当后端修复这个缺陷后可以去掉该逻辑

    return (
      <Flex
        justify="center"
        className={styles.recommandList}
        style={{ backgroundColor: itemBgColor }}>
        <ClassifyRecommandCell
          width={`calc(${cellWidth} - 0.24rem)`}
          data={{
            itemList: (classifyLinkRelationRespDtoList || []).filter((o) => !(o.displayMode === 1))
          }}
          config={{
            radius: '0.08rem'
          }}
          horizontal
          horizontalConfig={{
            size: {
              width: '2.04rem',
              height: `calc(2.04rem / 120 * 192)`
            },
            count: 3,
            type: 'scroll'
          }}
          onPress={(val) => {
            onPress && onPress(val);
          }}
        />
      </Flex>
    );
  };

  const renderHeader = () => {
    return (
      <Flex
        direction="row"
        align="center"
        justify="between"
        className={styles.headerContainer}
        onClick={() => onPress && onPress(data)}>
        <div className={styles.title} style={{ color: titleColor }}>
          <span>{title || ''}</span>
        </div>
        {!!moreText && (
          <div className={styles.more} style={{ color: moreColor }}>
            <span>{`${moreText || ''}`}</span>
            <span
              className={cx('iconfont', 'icon-arrow-right', styles.arrowRightIcon)}
              style={{ color: moreColor }}></span>
          </div>
        )}
      </Flex>
    );
  };

  const renderContent = () => {
    switch (relativeType) {
      case 0:
        return renderProducts();
      case 1:
        return renderRecommands();
    }
  };

  return (
    // 背景图尺寸 702*388
    <Flex
      direction="column"
      className={styles.wrapper}
      style={{
        borderRadius: `${(filletValue * 2) / 100}rem`
      }}>
      <ImgWrap w={cellWidth} h={`calc(${cellWidth} * 388 / 702)`} src={mainImgUrl || ''} />
      <Flex direction="column" align="start" className={styles.container}>
        {renderHeader()}
        {renderContent()}
      </Flex>
    </Flex>
  );
});

Main_3_5Grid.defaultProps = defaultProps;
Main_3_5Grid.propTypes = propTypes;

export default Main_3_5Grid;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/ClassifyCell/components/Main_3_5Grid/index.js